import React from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { DashboardCalendar } from '../../shared/ui-models/occupancy-dashboard.ui-model';
import { DashboardModel, DashboardTotals, GroupKey, SeriesPoint, UnitRow } from './engine';
import {
  Card,
  ChartTip,
  EmptyRow,
  GroupPicker,
  Metric,
  Pill,
  SearchBox,
  SortHeader,
  SortState
} from './components';
import { C, blockTone, fmtMoney, fmtNum, fmtPct, occTone } from './theme';
import { Formulas } from './Formulas';

export interface OccupancyTabProps {
  t: DashboardTotals;
  drag: number | null;
  model: DashboardModel;
  groupBy: GroupKey;
  setGroupBy: (g: GroupKey) => void;
  band: { x1: string; x2: string };
  todayLabel: string;
  rows: UnitRow[];
  sort: SortState;
  setSort: (s: SortState) => void;
  search: string;
  setSearch: (v: string) => void;
  cal: DashboardCalendar;
}

const GROUP_HEADERS = [
  'Group',
  'Units',
  'Net occupancy',
  'Gross occupancy',
  'Block share',
  'Sold',
  'Blocked',
  'Total',
  'ADR',
  'RevPAR'
];

export function OccupancyTab({
  t,
  drag,
  model,
  groupBy,
  setGroupBy,
  band,
  todayLabel,
  rows,
  sort,
  setSort,
  search,
  setSearch
}: OccupancyTabProps) {
  const unbilled = t.sold - t.ratedSold;

  return (
    <div className="mt-5 space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Metric
          label="Net occupancy — sold ÷ (total − blocked)"
          value={fmtPct(t.net)}
          sub={`${fmtNum(t.sold)} of ${fmtNum(t.available)} sellable nights`}
          accent={C.green}
        />
        <Metric
          label="Gross occupancy — sold ÷ total"
          value={fmtPct(t.gross)}
          sub={`${fmtNum(t.sold)} of ${fmtNum(t.total)} listed nights`}
        />
        <Metric
          label="Blocked drag on gross"
          value={drag === null ? '—' : `${drag.toFixed(1)} pts`}
          sub={`${fmtNum(t.blocked)} nights withheld`}
          accent={C.clay}
        />
        <Metric label="Sold nights" value={fmtNum(t.sold)} sub={`${t.units} units in scope`} />
        <Metric
          label="ADR"
          value={t.adr ? `EGP ${fmtMoney(t.adr)}` : '—'}
          sub={
            unbilled > 0
              ? `Billed sold nights · ${fmtNum(unbilled)} not billed yet`
              : 'Billed sold nights'
          }
        />
        <Metric
          label="RevPAR"
          value={t.revpar ? `EGP ${fmtMoney(t.revpar)}` : '—'}
          sub="Per listed night"
        />
      </div>

      <Card
        title="Occupancy across the window"
        note="Shaded band is the selected range. The dotted line is the report date — months to its right are still filling."
      >
        <div className="h-72 px-2 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={model.series} margin={{ top: 5, right: 20, bottom: 5, left: -10 }}>
              <CartesianGrid stroke="#EEF1F6" vertical={false} />
              <ReferenceArea x1={band.x1} x2={band.x2} fill={C.green} fillOpacity={0.06} />
              <ReferenceLine x={todayLabel} stroke={C.muted} strokeDasharray="3 3" />
              <XAxis
                dataKey="key"
                tickFormatter={(k: string) => model.series.find((m) => m.key === k)?.label ?? k}
                tick={{ fontSize: 10, fill: C.muted }}
                axisLine={{ stroke: C.line }}
                tickLine={false}
              />
              <YAxis
                unit="%"
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: C.muted }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={
                  <ChartTip
                    rows={(p: SeriesPoint) => [
                      { k: 'Net occupancy', v: fmtPct(p.net), color: C.green },
                      { k: 'Gross occupancy', v: fmtPct(p.gross), color: C.blue },
                      { k: 'Block share', v: fmtPct(p.blockPct), color: C.clay },
                      { k: 'Sold nights', v: fmtNum(p.sold) },
                      { k: 'Blocked nights', v: fmtNum(p.blocked) }
                    ]}
                  />
                }
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="net"
                name="Net occupancy"
                stroke={C.green}
                strokeWidth={2.5}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="gross"
                name="Gross occupancy"
                stroke={C.blue}
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="blockPct"
                name="Block share"
                stroke={C.clay}
                strokeWidth={1.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card
        title="Overview"
        note="Filtered scope rolled up."
        right={<GroupPicker groupBy={groupBy} setGroupBy={setGroupBy} />}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b" style={{ borderColor: C.line, background: '#FBFCFE' }}>
              <tr>
                {GROUP_HEADERS.map((h, i) => (
                  <th
                    key={h}
                    className={`px-4 py-3 text-[11px] font-semibold ${
                      i === 0 || (i >= 2 && i <= 4) ? 'text-left' : 'text-right'
                    }`}
                    style={{ color: C.muted }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.groups.map((g) => (
                <tr key={g.key} className="border-b last:border-0" style={{ borderColor: C.line }}>
                  <td className="px-4 py-3 font-medium">{g.key}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{g.units}</td>
                  <td className="px-4 py-3">
                    <Pill value={g.net} tone={occTone} />
                  </td>
                  <td className="px-4 py-3">
                    <Pill value={g.gross} tone={occTone} />
                  </td>
                  <td className="px-4 py-3">
                    <Pill value={g.blockPct} tone={blockTone} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtNum(g.sold)}</td>
                  <td
                    className="px-4 py-3 text-right tabular-nums"
                    style={{ color: g.blocked ? C.clay : C.muted }}
                  >
                    {fmtNum(g.blocked)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtNum(g.total)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {g.adr ? fmtMoney(g.adr) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {g.revpar ? fmtMoney(g.revpar) : '—'}
                  </td>
                </tr>
              ))}
              {model.groups.length === 0 && <EmptyRow cols={10} />}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="Unit detail"
        note={`${rows.length} units in scope`}
        right={<SearchBox search={search} setSearch={setSearch} />}
      >
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full text-sm">
            <thead
              className="sticky top-0 z-10 border-b"
              style={{ borderColor: C.line, background: '#FBFCFE' }}
            >
              <tr>
                <SortHeader column="name" sort={sort} setSort={setSort}>
                  Name
                </SortHeader>
                <SortHeader column="id" sort={sort} setSort={setSort}>
                  Unit ID
                </SortHeader>
                <SortHeader column="project" sort={sort} setSort={setSort}>
                  Project
                </SortHeader>
                <SortHeader column="area" sort={sort} setSort={setSort}>
                  Area
                </SortHeader>
                <SortHeader column="unitType" sort={sort} setSort={setSort}>
                  Unit type
                </SortHeader>
                <SortHeader column="gross" sort={sort} setSort={setSort}>
                  Overall occupancy
                </SortHeader>
                <SortHeader column="net" sort={sort} setSort={setSort}>
                  Occupancy without block dates
                </SortHeader>
                <SortHeader column="blocked" sort={sort} setSort={setSort} align="right">
                  Blocked
                </SortHeader>
                <SortHeader column="sold" sort={sort} setSort={setSort} align="right">
                  Booked
                </SortHeader>
                <SortHeader column="available" sort={sort} setSort={setSort} align="right">
                  Available
                </SortHeader>
                <SortHeader column="total" sort={sort} setSort={setSort} align="right">
                  Total
                </SortHeader>
                <SortHeader column="adr" sort={sort} setSort={setSort} align="right">
                  ADR
                </SortHeader>
                <SortHeader column="revpar" sort={sort} setSort={setSort} align="right">
                  RevPAR
                </SortHeader>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.roomId}
                  className="border-b last:border-0 hover:bg-slate-50"
                  style={{ borderColor: C.line }}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium">{r.name}</p>
                    <p className="text-xs" style={{ color: C.muted }}>
                      {r.view}
                      {' \xB7 '}
                      {r.landlord}
                    </p>
                  </td>
                  <td className="px-4 py-3" style={{ color: C.muted }}>
                    {r.id}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">{r.project}</td>
                  <td className="whitespace-nowrap px-4 py-3">{r.area}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {r.unitType}
                    <span className="ml-1.5 text-xs" style={{ color: C.muted }}>
                      {r.unitTypeGroup}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Pill value={r.gross} tone={occTone} />
                  </td>
                  <td className="px-4 py-3">
                    <Pill value={r.net} tone={occTone} />
                  </td>
                  <td
                    className="px-4 py-3 text-right tabular-nums"
                    style={{ color: r.blocked ? C.clay : C.muted }}
                  >
                    {r.blocked}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.sold}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.available}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.total}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.adr ? fmtMoney(r.adr) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.revpar ? fmtMoney(r.revpar) : '—'}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <EmptyRow cols={13} />}
            </tbody>
          </table>
        </div>
      </Card>
      <Formulas />
    </div>
  );
}
